import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useRef } from "react";
import LanyardCard from "@/components/Lanyard";
import useSteamStats from "@/hooks/useSteamStats";

const fallbackStats = [
  { label: "RANK", value: "SMFC", sub: "Supreme MFC" },
  { label: "HOURS", value: "4,820", sub: "Since 2015" },
  { label: "K/D", value: "1.42", sub: "Last 90d" },
  { label: "HS %", value: "58.3", sub: "Headshot ratio" },
];

const fallbackMaps = [
  { name: "MIRAGE", pct: 87 },
  { name: "DUST II", pct: 76 },
  { name: "INFERNO", pct: 71 },
  { name: "ANCIENT", pct: 62 },
];

const fallbackWeapons = [
  { name: "AK-47", tag: "Redline" },
  { name: "AWP", tag: "Wildfire" },
  { name: "USP-S", tag: "Kill Confirmed" },
  { name: "M4A1-S", tag: "Printstream" },
];

const chapterVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1] } },
};

function formatHours(h) {
  if (h == null) return "—";
  return Math.round(h).toLocaleString("en-US");
}

function formatPlaytime(minutes) {
  if (!minutes) return "—";
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// Animated counter component
function AnimCounter({ value, duration = 1.5 }) {
  const ref = useRef(null);
  const num = parseFloat(String(value).replace(/[^0-9.]/g, ""));
  const hasNumber = !isNaN(num);

  useEffect(() => {
    if (!hasNumber || !ref.current) return;
    const node = ref.current;
    const controls = animate(0, num, {
      duration,
      ease: [0.65, 0, 0.35, 1],
      onUpdate(v) {
        if (Number.isInteger(num)) {
          node.textContent = Math.round(v).toLocaleString("en-US");
        } else {
          node.textContent = v.toFixed(1);
        }
      },
    });
    return () => controls.stop();
  }, [num, hasNumber, duration]);

  if (!hasNumber) return <>{value}</>;

  // Preserve prefix/suffix (like %)
  const prefix = String(value).match(/^[^0-9]*/)?.[0] || "";
  const suffix = String(value).match(/[^0-9.]*$/)?.[0] || "";

  return (
    <>
      {prefix}
      <span ref={ref} />
      {suffix}
    </>
  );
}

export default function Stats() {
  const { loading, data } = useSteamStats();
  const live = data?.available === true;

  const stats = live
    ? [
        { label: "RANK", value: data.rank || "—", sub: data.rank ? "Manual (no public API)" : "Not set" },
        { label: "HOURS", value: formatHours(data.hours), sub: "On record, CS2" },
        { label: "K/D", value: data.kd ?? "—", sub: "All-time" },
        { label: "HS %", value: data.hs_pct ?? "—", sub: "Headshot ratio" },
      ]
    : fallbackStats;

  const maps =
    live && data.maps?.length
      ? data.maps.map((m) => ({ name: m.name, pct: m.pct }))
      : fallbackMaps;

  const weapons =
    live && data.weapons?.length
      ? data.weapons.map((w) => ({ name: w.name, tag: `${w.kills.toLocaleString("en-US")} KILLS` }))
      : fallbackWeapons;

  const lastPlayed = live ? data.last_played : null;

  const feedLabel = loading ? "SYNCING..." : live ? "◈ LIVE FEED" : "◈ DEMO DATA";

  return (
    <section id="loadout" className="relative px-6 md:px-12 py-24 md:py-32 section-glow section-scan" data-testid="stats-section">
      {/* Chapter heading */}
      <motion.div
        variants={chapterVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        className="relative z-10 max-w-6xl mb-14"
      >
        <div className="flex items-baseline gap-6 mb-6">
          <span className="font-mono text-sm text-[#F47B20]">CH.02</span>
          <span className="h-px flex-1 bg-gradient-to-r from-[#F47B20]/30 to-white/5 section-line" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
            LOADOUT / ARSENAL
          </span>
        </div>
        <h2 className="font-display font-black text-4xl sm:text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.9]">
          The <span className="text-stroke-orange">Kit</span>
          <br />
          <span className="text-stroke">Deployed</span>
        </h2>
        <p className="mt-6 max-w-xl font-mono text-sm text-white/60 leading-relaxed">
          [ 02 ] &nbsp; Weapons, presence, and telemetry pulled straight from
          field ops. {live ? "Live data synced from Steam." : "Demo data — connect Steam to go live."}
        </p>
      </motion.div>

      {/* Data ticker strip */}
      <div className="relative z-10 mb-6 overflow-hidden border-y border-white/5 py-2">
        <div className="ticker-scroll font-mono text-[9px] uppercase tracking-[0.3em] text-white/20 whitespace-nowrap">
          <span>RANK:SMFC ◈ K/D:1.42 ◈ HS:58% ◈ HOURS:4820 ◈ MAPS:247 ◈ HEADSHOTS:12840 ◈ MVPs:312 ◈ WINS:891 ◈</span>
          <span>RANK:SMFC ◈ K/D:1.42 ◈ HS:58% ◈ HOURS:4820 ◈ MAPS:247 ◈ HEADSHOTS:12840 ◈ MVPs:312 ◈ WINS:891 ◈</span>
        </div>
      </div>

      {/* Bento grid */}
      <div className="relative z-10 grid grid-cols-12 gap-4 md:gap-5">
        {/* Big stat card */}
        <motion.div
          variants={chapterVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="col-span-12 md:col-span-8 border border-white/10 bg-[#0d0d0f] p-6 md:p-8 clip-tactical noise-overlay glow-border"
          data-testid="stat-grid"
        >
          <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-6">
            <span>[ COMBAT.TELEMETRY ]</span>
            <span className={live ? "text-[#F47B20]" : "text-white/40"} data-testid="stats-feed-status">
              {feedLabel}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.65, 0, 0.35, 1] }}
                className="border-l border-white/10 pl-4 group"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                  {String(i + 1).padStart(2, "0")} / {s.label}
                </div>
                <div className="mt-2 font-display font-black text-3xl md:text-4xl text-[#F47B20] leading-none truncate group-hover:drop-shadow-[0_0_12px_rgba(244,123,32,0.4)] transition-all duration-500">
                  <AnimCounter value={s.value} />
                </div>
                <div className="mt-2 font-mono text-[11px] text-white/50">{s.sub}</div>
              </motion.div>
            ))}
          </div>

          {/* Map preference bars */}
          <div className="mt-10 grid grid-cols-1 gap-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
              MAP AFFINITY {live ? "/ BY WINS" : "/"}
            </div>
            {maps.map((m, i) => (
              <motion.div
                key={m.name}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * i }}
                className="flex items-center gap-3 md:gap-4 group"
              >
                <span className="font-mono text-xs text-white/70 w-24 group-hover:text-white transition-colors">{m.name}</span>
                <div className="flex-1 h-1.5 bg-white/5 relative overflow-hidden rounded-full">
                  <motion.span
                    initial={{ width: 0 }}
                    whileInView={{ width: `${m.pct}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, ease: [0.65, 0, 0.35, 1] }}
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#F47B20] to-[#D9C850] rounded-full"
                  />
                </div>
                <span className="font-mono text-xs text-[#F47B20] w-10 text-right">{m.pct}%</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Lanyard */}
        <div className="col-span-12 md:col-span-4">
          <LanyardCard />
        </div>

        {/* Last Played — full width banner */}
        {lastPlayed && (
          <motion.div
            variants={chapterVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="col-span-12 border border-white/10 bg-[#0d0d0f] p-5 md:p-6 clip-tactical noise-overlay glow-border flex items-center gap-5"
          >
            {lastPlayed.img_icon_url && (
              <img
                src={lastPlayed.img_icon_url}
                alt={lastPlayed.name}
                className="w-14 h-14 rounded-lg border border-white/10"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                [ LAST.PLAYED ]
              </div>
              <div className="mt-1 font-display font-bold text-xl md:text-2xl text-white truncate">
                {lastPlayed.name}
              </div>
              <div className="mt-1 font-mono text-[11px] text-white/50">
                {formatPlaytime(lastPlayed.playtime_2weeks)} last 2 weeks • {formatPlaytime(lastPlayed.playtime_forever)} total
              </div>
            </div>
            <div className="hidden md:flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#F47B20]">
              <span className="w-2 h-2 rounded-full bg-[#F47B20] pulse-dot" />
              RECENT
            </div>
          </motion.div>
        )}

        {/* Weapons */}
        <motion.div
          variants={chapterVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="col-span-12 md:col-span-8 border border-white/10 bg-[#0d0d0f] p-6 md:p-8 clip-tactical noise-overlay glow-border"
          data-testid="weapon-grid"
        >
          <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-6">
            <span>[ ARSENAL // {live ? "TOP KILLS" : "PREFERRED"} ]</span>
            <span>04 / ITEMS</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {weapons.map((w, i) => (
              <motion.div
                key={w.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.08 * i }}
                className="border border-white/10 bg-[#141416] p-4 md:p-4 hover:border-[#F47B20]/40 hover:bg-[#F47B20]/5 transition-all duration-500 group card-hover min-h-[100px]"
                data-testid={`weapon-${w.name.toLowerCase()}`}
              >
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 group-hover:text-[#F47B20]/60 transition-colors">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-3 font-display font-bold text-xl text-white group-hover:text-[#F47B20] transition-colors duration-300">
                  {w.name}
                </div>
                <div className="mt-1 font-mono text-[11px] text-white/50 uppercase group-hover:text-[#F47B20]/60 transition-colors">
                  ✦ {w.tag}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Playstyle */}
        <motion.div
          variants={chapterVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="col-span-12 md:col-span-4 border border-white/10 bg-[#0d0d0f] p-6 md:p-8 clip-tactical noise-overlay glow-border"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
            [ PROFILE.ROLE ]
          </div>
          <div className="font-display font-black text-4xl text-white leading-none">
            ENTRY <br />
            <span className="text-[#F47B20]">FRAGGER</span>
          </div>
          <div className="mt-4 font-mono text-xs text-white/55 leading-relaxed">
            Opens sites, calls smokes, dies gloriously. Turns
            deagle deagles into eco-round nightmares.
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {["AGGRO", "FLASH-SUPPORT", "IGL-LITE"].map((t, i) => (
              <motion.span
                key={t}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
                className="border border-white/15 px-2 py-1 min-h-[28px] font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:border-[#F47B20]/30 hover:text-[#F47B20]/70 transition-all duration-300 flex items-center"
              >
                {t}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
